import { parseExcelFileToOperations } from "../../mappers/excelToOperationMapper";
import { buildMachineLookupMap, resolveMachineId } from "./machineLookupService";
import { insertOperations } from "../../repositories/operationsRepository";
import {
  fetchUploadLogConflicts,
  insertUploadLogEntries,
  UploadLogKey,
} from "../../repositories/uploadLogRepository";
import { OperationInsertRow, UploadResult } from "../../types/operations";

function collectDistinctUploadKeys(
  operations: { machineType: string; startTime: string }[]
): UploadLogKey[] {
  const seen = new Set<string>();
  const keys: UploadLogKey[] = [];

  for (const op of operations) {
    const date = op.startTime.split("T")[0];
    const compositeKey = `${date}|${op.machineType}`;

    if (!seen.has(compositeKey)) {
      seen.add(compositeKey);
      keys.push({ upload_date: date, machine_type: op.machineType });
    }
  }

  return keys;
}

export async function uploadOperationsFromFile(
  file: File
): Promise<UploadResult> {
  // ---- 1. Parse Excel ----
  const { operations: parsedOperations, rejectedRowCount } =
    await parseExcelFileToOperations(file);

  // ---- 2. Resolve machine IDs ----
  const machineLookupMap = await buildMachineLookupMap();

  const resolvedRows: OperationInsertRow[] = [];
  const resolvedOperations: { machineType: string; startTime: string }[] = [];
  let unresolvedMachineCount = 0;

  for (const operation of parsedOperations) {
    const machineId = resolveMachineId(
      machineLookupMap,
      operation.machineType,
      operation.machineNumber
    );

    if (machineId === null) {
      unresolvedMachineCount += 1;
      continue;
    }

    resolvedRows.push({
      machine_id: machineId,
      shift: operation.shift,
      work_order: operation.workOrder,
      product_name: operation.productName,
      part_name: operation.partName,
      start_time: operation.startTime,
      end_time: operation.endTime,
      duration_seconds: operation.durationSeconds,
    });

    resolvedOperations.push({
      machineType: operation.machineType,
      startTime: operation.startTime,
    });
  }

  if (resolvedRows.length === 0) {
    return {
      fileName: file.name,
      totalRowsParsed: parsedOperations.length + rejectedRowCount,
      rowsInserted: 0,
      rowsRejected: rejectedRowCount + unresolvedMachineCount,
      conflictingUploadKeys: [],
    };
  }

  // ---- 3. Check upload_log for conflicts ----
  const uploadKeys = collectDistinctUploadKeys(resolvedOperations);
  const conflicts = await fetchUploadLogConflicts(uploadKeys);

  if (conflicts.length > 0) {
    return {
      fileName: file.name,
      totalRowsParsed: parsedOperations.length + rejectedRowCount,
      rowsInserted: 0,
      rowsRejected: rejectedRowCount + unresolvedMachineCount,
      conflictingUploadKeys: conflicts,
    };
  }

  // ---- 4. Insert all resolved rows ----
  const insertedCount = await insertOperations(resolvedRows);

  // ---- 5. Record the upload in upload_log ----
  await insertUploadLogEntries(uploadKeys);

  // ---- 6. Return summary ----
  return {
    fileName: file.name,
    totalRowsParsed: parsedOperations.length + rejectedRowCount,
    rowsInserted: insertedCount,
    rowsRejected: rejectedRowCount + unresolvedMachineCount,
    conflictingUploadKeys: [],
  };
}
